import { useCallback, useEffect, useMemo } from 'react';
import { useInfiniteQuery, useQueries, useQuery, useQueryClient, keepPreviousData, type QueryKey } from '@tanstack/react-query';
import { useApi } from '../../../lib/api';
import { ApiProblemError, fetchJson, fetchRepSafeJson } from '../../../lib/apiFetchJson';
import { validateResponse } from '../../../lib/validateResponse';
import { consumeTextStream } from '../../../lib/sse-text-stream';
import { ContactsResponseSchema, CONTACT_SIGNAL_FIELD_KEYS, type ContactsResponse } from '@shared/flow/contact-view';
import { FlowContactTimelineSchema, type FlowContactTimeline } from '@shared/flow/contact-timeline';
import { FlowEntitySignalsResponseSchema, type FlowEntitySignalsResponse } from '@shared/flow/entity-signals';
import type { ContactIntelContactFields } from '@shared/flow/contact-intel';
import type { FilterGroup, GroupLogic } from '@shared/flow/filter';
import type { PlayPeopleSection } from '@shared/flow/play-people';
import { FlowAccountIntelSchema, type FlowAccountIntel } from '@shared/flow/account-intel';
import {
  ContactStateListResponseSchema,
  ContactStateDetailSchema,
  type ContactStateListRow,
  type ContactStateDetail,
  type ContactStatePill,
} from '@shared/schemas/flow-contact-state';
import { FLOW_CONTACT_STATE_KEY, FLOW_CONTACT_STATE_DETAIL_KEY, FLOW_CONTACT_SIGNALS_KEY, FLOW_CONTACT_TIMELINE_KEY } from '../contact-state-keys';

/**
 * No `scope` (U5 / R2), and no `play_id`/`bucket` either (plan
 * 2026-09-10-001 U5): the contacts universe is the whole mirror, and a
 * play's roster is no longer a hidden server-side narrowing but an ordinary,
 * visible filter group over `signal_subtype` (the play's NAME — plan
 * 2026-09-10-001 amendment A4) + the Signal Status predicates
 * (`buildPlayScopeGroup`, `@shared/flow/plays-view`). The only
 * narrowing this hook applies on its own is `accountId` (the drill-down
 * roster); everything else rides `filters` — including the ownership chip,
 * which travels as the `{me}` token and is resolved per acting user
 * server-side.
 */
export interface FlowContactsParams {
  accountId?: string;
  /**
   * Single-contact PK lookup (U5, bead .11) — the Signals detail panel's
   * mount, sent as `contact_id` on the wire. A PK equality, not an
   * ownership predicate: it narrows the response to (at most) one row but
   * carries no scope semantics of its own. Only the Signals detail panel
   * supplies this today.
   */
  contactId?: string;
  /**
   * The selected play's section (plan 2026-09-25-002 U6; was plan
   * 2026-09-24-005 U16's `notInPlay`) — one of the Plays tab's three
   * "People in <Play>" mounts. Sent as `play_id` / `play_type` /
   * `play_section`: the contacts route then narrows the universe to that
   * section of the play and projects the `play_*` columns off its `pm` join.
   * All three halves are in the queryKey, so switching plays or sections is
   * a fresh page-1 fetch.
   */
  playScope?: { playId: string; playType: string; section: PlayPeopleSection };
  search: string;
  filters: FilterGroup[];
  groupLogic: GroupLogic;
  sort: { field: string; dir: 'ASC' | 'DESC' };
  /** Ordered grouping levels (PR1 U4/D7/D8, R5) — sent as `group_by=a,b,c`. */
  groupBy?: string[];
  /**
   * The view's VISIBLE column keys (`machine.working.columns`) — read ONLY
   * to decide whether to send `include=signals` (plan
   * `2026-09-13-002-terra-signals-perf-robust-plan.md` B1 U2): when a
   * `signal_*`/`has_responded` column is on the page but no signal
   * filter/sort/group_by is active, the server has no other signal the
   * request needs Signal values, so it drops the `sig` display join unless
   * told to keep it. Optional/omittable — a caller with no column state
   * (there isn't one today) simply never sends the param, same as before
   * this plan.
   */
  columns?: string[];
  /**
   * Gate the fetch (default true). ContactFlowTable's global mount holds
   * this false until the role-identity query settles and the role-default
   * filter has been seeded, so the first fetch never fires with unseeded
   * params (see ContactFlowTable.tsx's seeding effect).
   */
  enabled?: boolean;
}

/** Keyset-paginated contacts list — every param change requeries page 1. */
export function useFlowContacts(params: FlowContactsParams) {
  const { apiFetch } = useApi();

  // R3 (plan "B1 review follow-ups") — the derived boolean itself, computed
  // ONCE and used for both the queryKey and the request. Before this fix,
  // `columns` was (correctly) excluded from the key — a column reorder must
  // not blow away the cached page — but the derived `includeSignals` bit,
  // which DOES change the response shape (real values vs. NULLs), was never
  // added in its place: toggling a signal column visible re-rendered with
  // the SAME cached page (all-NULL signal cells) until some OTHER param
  // changed forced a refetch.
  const includeSignals = params.columns?.some(c => CONTACT_SIGNAL_FIELD_KEYS.includes(c)) ?? false;

  return useInfiniteQuery<ContactsResponse>({
    enabled: params.enabled ?? true,
    queryKey: ['flow-contacts', {
      accountId: params.accountId ?? null,
      contactId: params.contactId ?? null,
      playScope: params.playScope
        ? { playId: params.playScope.playId, playType: params.playScope.playType, section: params.playScope.section }
        : null,
      search: params.search,
      filters: params.filters,
      groupLogic: params.groupLogic,
      sort: params.sort,
      groupBy: params.groupBy ?? [],
      // `columns` itself deliberately NOT in the key (a reorder/add of a
      // NON-signal column must not blow away the cached page) — but the ONE
      // bit it can affect the response with IS: `includeSignals` changes
      // whether the server keeps the `sig` display join.
      includeSignals,
    }],
    queryFn: async ({ pageParam }) => {
      const qs = new URLSearchParams({
        sort: params.sort.field,
        dir: params.sort.dir,
        group_logic: params.groupLogic,
      });
      if (params.accountId) qs.set('account_id', params.accountId);
      if (params.contactId) qs.set('contact_id', params.contactId);
      if (params.playScope) {
        qs.set('play_id', params.playScope.playId);
        qs.set('play_type', params.playScope.playType);
        qs.set('play_section', params.playScope.section);
      }
      if (params.search.trim()) qs.set('search', params.search.trim());
      if (params.filters.some(g => g.conditions.length > 0)) qs.set('filters', JSON.stringify(params.filters));
      if (params.groupBy && params.groupBy.length > 0) qs.set('group_by', params.groupBy.join(','));
      // B1 U2 / R3 — sent iff a Signal column is currently VISIBLE, so the
      // server keeps the `sig` display join for a rep who added one from
      // the Columns picker even with no signal filter/sort/group_by driving
      // the need. `include` is now a single literal server-side
      // (`z.literal('signals')`, R3) — no comma-list to build here.
      if (includeSignals) qs.set('include', 'signals');
      if (typeof pageParam === 'string' && pageParam) qs.set('cursor', pageParam);

      // U13 (finding 4-5) — `fetchRepSafeJson` (web/app-home/src/lib/apiFetchJson.ts)
      // covers the rejected-fetch and bad-JSON paths this used to leave raw
      // (`query.error.message` was a bare "Failed to fetch" or a raw
      // SyntaxError before this fix), on top of the existing `!res.ok`
      // handling.
      const body = await fetchRepSafeJson(apiFetch, `/api/flow/contacts?${qs.toString()}`, {
        timeoutMs: 30_000,
        fallback: "Couldn't load contacts right now.",
      });
      return validateResponse(ContactsResponseSchema, body, 'GET /api/flow/contacts');
    },
    initialPageParam: null as string | null,
    getNextPageParam: last => last.next_cursor,
    // Params live in the queryKey — any control change refetches page 1.
    // keepPreviousData keeps the previous rows on screen (dimmed) during the
    // refetch instead of flashing an empty "Loading…" state.
    staleTime: Infinity,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
  });
}

export function useFlowContactTimeline(contactId: string | null) {
  const { apiFetch } = useApi();
  return useQuery<FlowContactTimeline>({
    queryKey: [...FLOW_CONTACT_TIMELINE_KEY, contactId],
    enabled: !!contactId,
    staleTime: Infinity,
    queryFn: async () => {
      return fetchJson(apiFetch, FlowContactTimelineSchema, `/api/flow/contacts/${contactId}/timeline`, { timeoutMs: 30_000 });
    },
  });
}

/**
 * Batch pill fetch for the cross-app "Cross-app state" column
 * (POST /api/flow/contacts/state — pills only, no facts).
 *
 * ONE QUERY PER PAGE (Blake's PR #1088 review fix — the previous version
 * re-POSTed the FULL accumulated contact-id set on every page scroll, which
 * 400'd against the resolver's 500-id cap past page 2 of the 500-row-paged
 * "All Contacts" view and blanked EVERY visible row's pills, not just the
 * overflow). `pages` is `useFlowContacts`'s `query.data.pages` — each
 * element is one keyset page (≤FLOW_PAGE_SIZE rows), and pages only ever
 * APPEND (earlier pages never reorder/resize once loaded), so deriving one
 * chunk per PAGE gives inherently-stable, inherently-≤500 chunks whose
 * query keys don't churn as later pages load. Do NOT flatten `pages` into
 * one id array and re-chunk it by index — that shifts chunk boundaries
 * (and therefore query keys) every time a page loads, which would refetch
 * everything on every scroll instead of just the new page.
 *
 * Each chunk is its own `useQueries` entry keyed
 * `[...FLOW_CONTACT_STATE_KEY, <that page's ids joined>]` — nested under the
 * shared root so EnrollQueueContext's prefix invalidation
 * (`invalidateQueries({ queryKey: FLOW_CONTACT_STATE_KEY })`, exact: false
 * by default) refetches every chunk automatically, same as the old single-
 * query version.
 *
 * Failure isolation: one page's POST rejecting only means that page's ids
 * get no map entries — every other (already-succeeded) page's pills stay
 * rendered. There is no shared/global empty-map fallback anymore.
 *
 * The resolver's 500-id cap (ContactStateListRequestSchema,
 * src/api/flow-contact-state.ts) can no longer be hit by pagination: each
 * chunk is one page (≤FLOW_PAGE_SIZE) so the server cap can't be hit by
 * pagination.
 *
 * staleTime: Infinity + no polling (project caching rule — cache
 * indefinitely, invalidate on mutation). Rows whose id is absent from a
 * chunk's response get no pills — absent IS "no signal" per the resolver
 * contract, not a loading state.
 */
export interface ContactStatePillsResult {
  pillsByContactId: Map<string, ContactStatePill[]>;
  /**
   * True once every currently-requested chunk has SETTLED — resolved to
   * success or error, per-chunk `!isPending` (a chunk with zero ids never
   * runs at all — `enabled: ids.length > 0` below — so it's trivially
   * counted as settled rather than stuck pending forever). Consumed by
   * ContactFlowTable's account-mount "Terra Actions first" partition
   * (rankByState.ts): pills arrive asynchronously per chunk as pages load,
   * so reordering on every partial chunk update would visibly row-jump.
   * Waiting for isSettled gives exactly ONE reorder, once the chunks for
   * whatever's currently loaded have all come back (success OR failure —
   * a failed chunk's contacts just stay in the "no notes" partition, same
   * as any other absent-pills contact).
   */
  isSettled: boolean;
}

function useContactStatePillsInternal(pages: { rows: { id: string }[] }[]): ContactStatePillsResult {
  const { apiFetch } = useApi();

  // One chunk per page, in page order. Each page's own row-id list is the
  // chunk's contact-id set AND (joined) its stable queryKey suffix — no
  // separate hashing needed since a page's ids never reorder once loaded.
  const chunkIdLists = useMemo(() => pages.map(page => page.rows.map(r => r.id)), [pages]);

  const chunkQueries = useQueries({
    queries: chunkIdLists.map(ids => ({
      queryKey: [...FLOW_CONTACT_STATE_KEY, ids.join(',')],
      enabled: ids.length > 0,
      staleTime: Infinity,
      queryFn: async (): Promise<ContactStateListRow[]> => {
        return fetchJson(apiFetch, ContactStateListResponseSchema, '/api/flow/contacts/state', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ contactIds: ids }),
        });
      },
    })),
  });

  return useMemo(() => {
    const map = new Map<string, ContactStatePill[]>();
    for (const chunk of chunkQueries) {
      // A rejected/still-loading chunk has `data: undefined`. This guard
      // just means that chunk's ids stay absent from the map — isolated from
      // every other (successful) chunk.
      const list = Array.isArray(chunk.data) ? chunk.data : [];
      for (const row of list) map.set(row.contact.contactId, row.pills);
    }
    const isSettled = chunkIdLists.every((ids, i) => ids.length === 0 || !chunkQueries[i].isPending);
    return { pillsByContactId: map, isSettled };
  }, [chunkQueries, chunkIdLists]);
}

/**
 * Batch pill fetch for the cross-app "Cross-app state" column
 * (POST /api/flow/contacts/state — pills only, no facts).
 *
 * ONE QUERY PER PAGE (Blake's PR #1088 review fix — the previous version
 * re-POSTed the FULL accumulated contact-id set on every page scroll, which
 * 400'd against the resolver's 500-id cap past page 2 of the 500-row-paged
 * "All Contacts" view and blanked EVERY visible row's pills, not just the
 * overflow). `pages` is `useFlowContacts`'s `query.data.pages` — each
 * element is one keyset page (≤FLOW_PAGE_SIZE rows), and pages only ever
 * APPEND (earlier pages never reorder/resize once loaded), so deriving one
 * chunk per PAGE gives inherently-stable, inherently-≤500 chunks whose
 * query keys don't churn as later pages load. Do NOT flatten `pages` into
 * one id array and re-chunk it by index — that shifts chunk boundaries
 * (and therefore query keys) every time a page loads, which would refetch
 * everything on every scroll instead of just the new page.
 *
 * Each chunk is its own `useQueries` entry keyed
 * `[...FLOW_CONTACT_STATE_KEY, <that page's ids joined>]` — nested under the
 * shared root so EnrollQueueContext's prefix invalidation
 * (`invalidateQueries({ queryKey: FLOW_CONTACT_STATE_KEY })`, exact: false
 * by default) refetches every chunk automatically, same as the old single-
 * query version.
 *
 * Failure isolation: one page's POST rejecting only means that page's ids
 * get no map entries — every other (already-succeeded) page's pills stay
 * rendered. There is no shared/global empty-map fallback anymore.
 *
 * The resolver's 500-id cap (ContactStateListRequestSchema,
 * src/api/flow-contact-state.ts) can no longer be hit by pagination: each
 * chunk is one page (≤FLOW_PAGE_SIZE) so the server cap can't be hit by
 * pagination.
 *
 * staleTime: Infinity + no polling (project caching rule — cache
 * indefinitely, invalidate on mutation). Rows whose id is absent from a
 * chunk's response get no pills — absent IS "no signal" per the resolver
 * contract, not a loading state.
 *
 * Returns just the pills map — kept for callers (and its own direct test
 * coverage, contact/__tests__/hooks.test.tsx) that don't need settle status.
 * ContactFlowTable's account-mount partition needs settle status too, so it
 * calls `useContactStatePillsWithSettle` below instead — both delegate to
 * the SAME internal hook (one subscription, one fetch), never a second POST.
 */
export function useContactStatePills(pages: { rows: { id: string }[] }[]): Map<string, ContactStatePill[]> {
  return useContactStatePillsInternal(pages).pillsByContactId;
}

/**
 * Same batch pill fetch as `useContactStatePills`, plus `isSettled` — see
 * `ContactStatePillsResult`'s doc comment above for what "settled" means and
 * why ContactFlowTable's account-mount ranking needs it. Not "a second
 * fetch": both exported hooks call the same internal `useQueries` chunking
 * logic, and react-query dedupes identical query keys across subscribers —
 * a mount only ever calls ONE of these two hooks, never both.
 */
export function useContactStatePillsWithSettle(pages: { rows: { id: string }[] }[]): ContactStatePillsResult {
  return useContactStatePillsInternal(pages);
}

/** Detail read (pills + facts) for the ContactDetail "Cross-app state" panel. */
export function useContactStateDetail(contactId: string | null) {
  const { apiFetch } = useApi();
  return useQuery<ContactStateDetail | null>({
    queryKey: [...FLOW_CONTACT_STATE_DETAIL_KEY, contactId],
    enabled: !!contactId,
    staleTime: Infinity,
    retry: false,
    queryFn: async ({ signal }) => {
      try {
        return await fetchJson(apiFetch, ContactStateDetailSchema, `/api/flow/contacts/${contactId}/state`, { signal });
      } catch (err) {
        if (err instanceof ApiProblemError && err.kind === 'http' && err.status === 404) return null;
        throw err;
      }
    },
  });
}

/**
 * "Actions" tab read (plan §2b item 3, D1) — the per-contact `terra_signals`
 * list (`GET /api/flow/contacts/:id/signals`). `staleTime: Infinity` per the
 * project caching rule; a fresh queue item invalidates this via
 * `FLOW_CONTACT_SIGNALS_KEY` (see `EnrollQueueContext.tsx`'s
 * `invalidateContactState`), not a TTL.
 */
export function useFlowContactSignals(contactId: string | null) {
  const { apiFetch } = useApi();
  return useQuery<FlowEntitySignalsResponse>({
    queryKey: [...FLOW_CONTACT_SIGNALS_KEY, contactId],
    enabled: !!contactId,
    staleTime: Infinity,
    retry: false,
    queryFn: async () => {
      return fetchJson(apiFetch, FlowEntitySignalsResponseSchema, `/api/flow/contacts/${contactId}/signals`, { timeoutMs: 30_000 });
    },
  });
}

export interface UseContactIntelSummaryResult {
  /** The streamed (or cached) summary text, accumulated so far. */
  text: string;
  /** True while a generation's request is in flight (initial or regenerate). */
  isStreaming: boolean;
  /** Set when the request or the stream itself failed. */
  error: string | null;
  /** Clears the cached entry and refetches — always a fresh POST. */
  regenerate: () => void;
}

/**
 * What the shared streaming core returns. Superset of
 * `UseContactIntelSummaryResult` by exactly one field: `isEmpty`, which only
 * a caller that opted into `notFoundIsEmpty` can ever see set (the contact
 * route has no 404-means-empty contract, so its wrapper drops it).
 */
export interface UseStreamedSummaryResult extends UseContactIntelSummaryResult {
  /**
   * True when the producer answered "there is nothing to summarize" (a 404
   * under `notFoundIsEmpty`) — an outcome, not a failure: `error` is null and
   * `text` is empty. The UI shows its own "no intelligence yet" copy.
   */
  isEmpty: boolean;
}

/**
 * React Query key root for the Contact Intelligence stream (U5/D8, plan
 * `plans/2026-09-23-001-contact-actions-play-deeplink-and-hardening-plan.md`).
 * STABLE per contact — it deliberately carries no `generation` segment, so a
 * collapse/reopen of the panel can never re-point at an older generation's
 * cache entry (R15).
 */
export const FLOW_CONTACT_INTEL_KEY = ['flow-contact-intel'] as const;

/**
 * The cached Contact Intelligence value. `complete` is the whole point: it
 * separates "the model finished and sent its `done` frame" from "the stream
 * stopped" (unmount-abort, a truncating proxy, a dropped connection). An
 * entry with `complete: false` is a partial that must never be served as a
 * finished summary on the next open (R14).
 *
 * Not a Zod schema: this never crosses a trust boundary — it is assembled
 * locally from `consumeTextStream`'s already-validated frames and only ever
 * read back out of our own query cache.
 */
export interface ContactIntelCacheEntry {
  text: string;
  complete: boolean;
  /**
   * Set only by a `notFoundIsEmpty` caller whose producer answered 404 —
   * "this entity has nothing to summarize" (the Account Intelligence Summary
   * contract, plan
   * `plans/2026-09-23-002-rep-prompt-overrides-and-account-intelligence-summary-plan.md`
   * decision 6/7). Absent everywhere else, which is why it is optional rather
   * than a third required boolean: the contact stream has no such outcome and
   * its cached entries stay exactly `{ text, complete }`.
   */
  empty?: boolean;
}

interface StreamedSummaryOptions {
  /** Full react-query key for this entity's entry (stable, no generation segment). */
  queryKey: QueryKey;
  /** Already ANDed with "do we have an entity id?" by the caller. */
  enabled: boolean;
  /** Issues the POST. Receives react-query's abort signal so an unmount cancels the stream. */
  request: (signal: AbortSignal) => Promise<Response>;
  /**
   * When true, a 404 response is a successful "nothing to summarize" outcome
   * (cached `{ text: '', complete: true, empty: true }`, no error) instead of
   * a stream error.
   */
  notFoundIsEmpty: boolean;
}

/**
 * The streaming core shared by `useContactIntelSummary` and
 * `useAccountIntelSummary` (plan
 * `plans/2026-09-23-002-rep-prompt-overrides-and-account-intelligence-summary-plan.md`
 * U8, decision 8) — extracted from `useContactIntelSummary` verbatim rather
 * than copy-pasted into a second hook, so the hardening the contact hook
 * accumulated (R14 partial-as-final, R15 stale-generation) can never drift
 * between the two surfaces. The contact suite in
 * `__tests__/hooks.test.tsx` is the characterization test for this
 * extraction and passes unchanged.
 *
 * "Draft State Under SSE" applied via the react-query cache itself, NOT
 * `useState` + reseed: `queryFn` streams the SSE response and calls
 * `queryClient.setQueryData(key, accumulated)` per delta, so `data` updates
 * live while the fetch is in flight (react-query v5 marks a query `success`
 * on `setQueryData`). A parent re-render (the v4 tree's `artifact:updated`
 * SSE churn) neither restarts nor wipes the stream, because nothing here
 * reseeds from a prop on every render — the query key only changes when the
 * entity id does.
 */
function useStreamedSummary({
  queryKey,
  enabled,
  request,
  notFoundIsEmpty,
}: StreamedSummaryOptions): UseStreamedSummaryResult {
  const queryClient = useQueryClient();

  const query = useQuery<ContactIntelCacheEntry>({
    queryKey,
    enabled,
    staleTime: Infinity,
    retry: false,
    queryFn: async ({ signal }) => {
      const res = await request(signal);

      // Checked BEFORE `consumeTextStream`, which treats any non-ok response
      // as a stream error — under this contract a 404 is a finished,
      // error-free outcome, so it must never reach the consumer.
      if (notFoundIsEmpty && res.status === 404) {
        return { text: '', complete: true, empty: true };
      }

      let streamError: string | null = null;
      let sawDone = false;
      const finalText = await consumeTextStream(res, {
        // Live-stream into the cache as `complete: false` — if the request is
        // aborted mid-stream (panel collapsed), whatever react-query keeps is
        // then self-evidently a partial.
        onDelta: (acc) => queryClient.setQueryData<ContactIntelCacheEntry>(queryKey, { text: acc, complete: false }),
        onDone: () => { sawDone = true; },
        onError: (message) => { streamError = message; },
      });

      if (finalText === null || streamError) {
        throw new Error(streamError ?? 'Failed to load intelligence summary');
      }
      // A body that ends without a `done` frame (truncating proxy, dropped
      // upstream connection) is NOT a finished summary, even though the read
      // completed without throwing.
      return { text: finalText, complete: sawDone };
    },
  });

  /**
   * Resume check on (re)mount: a cached entry left `complete: false` by an
   * interrupted stream must refetch rather than be served forever under
   * `staleTime: Infinity`.
   *
   * Deliberately holds NO lifetime ref (see
   * `docs/v4-system/strictmode-resilient-effects.md`) — the guard reads live
   * cache state instead, which makes StrictMode's setup→cleanup→setup
   * double-invoke naturally idempotent: `invalidateQueries` flips
   * `fetchStatus` to `'fetching'` synchronously, so the second setup bails on
   * the `!== 'idle'` check and cannot cancel-and-restart the stream it just
   * started. A ref would have had to reset at the top of the effect anyway,
   * which would not have guarded anything.
   */
  useEffect(() => {
    if (!enabled) return;
    const state = queryClient.getQueryState<ContactIntelCacheEntry>(queryKey);
    if (!state || state.fetchStatus !== 'idle') return;
    if (state.data && !state.data.complete) {
      void queryClient.invalidateQueries({ queryKey, exact: true });
    }
  }, [enabled, queryClient, queryKey]);

  const { refetch } = query;
  const regenerate = useCallback(() => {
    // Reset the entry first (react-query ignores a `setQueryData(key,
    // undefined)`, so an explicit empty-and-incomplete entry is the reset),
    // then force a fresh request — `cancelRefetch` so a double-click cannot
    // leave two streams writing into the same cache key.
    queryClient.setQueryData<ContactIntelCacheEntry>(queryKey, { text: '', complete: false });
    void refetch({ cancelRefetch: true });
  }, [queryClient, queryKey, refetch]);

  return {
    text: query.data?.text ?? '',
    isStreaming: query.isFetching,
    error: query.isError
      ? (query.error instanceof Error ? query.error.message : String(query.error))
      : null,
    regenerate,
    isEmpty: query.data?.empty === true,
  };
}

/**
 * Contact Intelligence panel read (D6, plan
 * `plans/2026-09-22-contact-actions-intelligence-implementation-plan.md`
 * §3b item 2). Since plan
 * `plans/2026-09-23-002-rep-prompt-overrides-and-account-intelligence-summary-plan.md`
 * U8 this is a thin wrapper over `useStreamedSummary` — same signature, same
 * `UseContactIntelSummaryResult`, same behavior; see that hook's doc comment
 * for the cache-as-draft-state mechanics.
 *
 * `notFoundIsEmpty: false`: this route has no "nothing to summarize" outcome
 * (it composes its prompt from the contact fields in the request body, so it
 * cannot 404 on missing data), and a 404 here is a genuine failure the rep
 * should see — the account route's contract is the one that differs.
 *
 * U5/D8 (plan `2026-09-23-001-contact-actions-play-deeplink-and-hardening-
 * plan.md`) hardening preserved through the extraction: the key is stable per
 * contact (no `generation` segment — a reopened panel used to fall back to
 * generation 0's cache entry and show a stale summary, R15), the cached value
 * carries a `complete` flag, and the mount effect turns an interrupted stream
 * into a refetch instead of presenting a partial as final (R14).
 */
export function useContactIntelSummary(
  contact: ContactIntelContactFields | null,
  enabled: boolean,
): UseContactIntelSummaryResult {
  const { apiFetch } = useApi();
  const contactId = contact?.id ?? null;
  const queryKey = useMemo(() => [...FLOW_CONTACT_INTEL_KEY, contactId] as const, [contactId]);

  const request = useCallback(
    (signal: AbortSignal) => apiFetch(`/api/flow/contacts/${contactId}/intel-summary`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ contact }),
      signal,
      timeoutMs: 60_000,
    }),
    [apiFetch, contactId, contact],
  );

  const { text, isStreaming, error, regenerate } = useStreamedSummary({
    queryKey,
    enabled: enabled && !!contact,
    request,
    notFoundIsEmpty: false,
  });

  return { text, isStreaming, error, regenerate };
}

/**
 * React Query key root for the Account Intelligence Summary stream (plan
 * `plans/2026-09-23-002-rep-prompt-overrides-and-account-intelligence-summary-plan.md`
 * U8). STABLE per account for the same reason the contact root is (R15), and
 * exported so a prompt save/revert can drop every account's cached summary in
 * one call (`useUserSetting.ts`, decision 12).
 */
export const FLOW_ACCOUNT_INTEL_SUMMARY_KEY = ['flow-account-intel-summary'] as const;

/**
 * Account Intelligence panel "Summary" tab read (plan
 * `plans/2026-09-23-002-rep-prompt-overrides-and-account-intelligence-summary-plan.md`
 * U8, decisions 7/8) — streams `POST /api/flow/accounts/:accountId/intel-summary`
 * (`src/api/flow/flow-accounts-intel-summary.ts`) through the same core as
 * `useContactIntelSummary`. Body is `{}`: the account id rides the URL and
 * the digest is assembled server-side from the `account_intelligence` mirror
 * row, so unlike the contact route there is nothing for the client to send.
 *
 * 404 CONTRACT (`notFoundIsEmpty: true`): the route answers
 * `problem 404 'No intelligence for this account yet'` — before any SSE
 * header — when the account has no mirror row, or a row whose parsed state
 * has zero of the digest's sections. That is a normal state for most
 * accounts, not a failure, so it is cached as
 * `{ text: '', complete: true, empty: true }` and surfaced as `isEmpty` with
 * `error: null`; `complete: true` keeps the resume-on-remount effect from
 * re-POSTing it on every reopen.
 *
 * `accountId` is the SFDC 18-char id (`row.account_id` / `account.id`) —
 * what `AccountIntelligence.salesforce_account_id` holds.
 */
export function useAccountIntelSummary(
  accountId: string | null,
  enabled: boolean,
): UseStreamedSummaryResult {
  const { apiFetch } = useApi();
  const queryKey = useMemo(() => [...FLOW_ACCOUNT_INTEL_SUMMARY_KEY, accountId] as const, [accountId]);

  const request = useCallback(
    (signal: AbortSignal) => apiFetch(`/api/flow/accounts/${accountId}/intel-summary`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
      signal,
      timeoutMs: 60_000,
    }),
    [apiFetch, accountId],
  );

  return useStreamedSummary({
    queryKey,
    enabled: enabled && !!accountId,
    request,
    notFoundIsEmpty: true,
  });
}

/** Root of the Flow account-intelligence cache key — import, never re-type the literal. */
export const FLOW_ACCOUNT_INTEL_KEY = ['flow-account-intel'] as const;

export function useFlowAccountIntel(accountId: string | null) {
  const { apiFetch } = useApi();
  return useQuery<FlowAccountIntel | null>({
    queryKey: [...FLOW_ACCOUNT_INTEL_KEY, accountId],
    enabled: !!accountId,
    staleTime: Infinity,
    retry: false,
    queryFn: async () => {
      try {
        return await fetchJson(apiFetch, FlowAccountIntelSchema, `/api/flow/accounts/${accountId}/intel`);
      } catch (err) {
        if (err instanceof ApiProblemError && err.kind === 'http' && err.status === 404) return null;
        throw err;
      }
    },
  });
}
